<script lang="ts" setup>
import { computed, h, onMounted, ref } from "vue";
import { authService } from "@/app/services/AuthService";
import AppButton from "@/components/atoms/button/AppButton.vue";
import AppButtonOutline from "@/components/atoms/button/AppButtonOutline.vue";
import FormInvalidFeedback from "@/components/atoms/other/FormInvalidFeedback.vue";
import FeatherIcon from "@/components/atoms/other/FeatherIcon.vue";
import Header from "@/components/organisms/common/Header.vue";
import ExternalLink from "@/const/externalLink";
import { authStore } from "@/store/auth";
import { AbTest3113 } from "@/app/services/AbTestService";
import { useLoader } from "@/utils/loader";
import { useModal } from "@/utils/modal";
import { useCustomRouter } from "@/utils/router";

const modal = useModal();
const loader = useLoader();
const customRouter = useCustomRouter();
const phoneNumber = ref("");
const phoneNumberInputRef = ref<HTMLInputElement | null>(null);
const phoneNumberWithoutSpace = computed((): string => {
  return phoneNumber.value.replace(/\s/g, "");
});

onMounted(() => {
  if (phoneNumberInputRef.value) phoneNumberInputRef.value.focus();
});

// この画面を表示した際に新規登録に関するstoreの情報をクリアする
authStore.clearRegisterInfo();

async function nextStep() {
  try {
    loader.show();
    // NOTE: 不正なトークンがあった場合、このタイミングで削除しなければAPIに渡されてしまいエラーになってしまう。
    // そのためこのタイミングで認証情報は一度リセットさせる
    authStore.logout();

    const status = await authService.fetchAccountStatus(
      phoneNumberWithoutSpace.value
    );
    // 停止済みユーザーの場合
    if (status === "ban") {
      showInactiveUserModal();
      return;
    }

    AbTest3113.registerEvent(AbTest3113.eventTypes.enterPhoneNumberNext);

    // 認証が必要なユーザーの場合、電話番号認証画面へ
    if (status === "requiredPhoneNumberAuth") {
      customRouter.pushIgnoreError({
        name: "RegisterPhoneNumberAuth",
        query: { phoneNumber: phoneNumberWithoutSpace.value }
      });
      return;
    }

    // 上記以外（アクティブな登録済みユーザー）の場合、ログイン画面へ
    customRouter.pushIgnoreError({
      name: "Login",
      query: { phoneNumber: phoneNumberWithoutSpace.value }
    });
    return;
  } catch {
    // serviceNameが不正な場合
    await modal.msgBoxOk("不正な操作です", {
      centered: true
    });
  } finally {
    loader.hide();
  }
}

function showInactiveUserModal() {
  const messageVNode = h("div", [
    h("p", "一時的に利用停止中のためログインできません。"),
    h("p", "利用停止期間を過ぎましたら、アカウントが復帰されます。"),
    h("p", [
      "アカウントが復帰されない場合は、運営事務局へお問い合わせください。",
      h(
        AppButtonOutline,
        {
          class: "mb-1 mt-3",
          href: ExternalLink.CONTACT,
          size: "small",
          block: true
        },
        "お問い合わせフォーム"
      )
    ])
  ]);
  // @ts-ignore
  modal.msgBoxOk(messageVNode, {
    centered: true
  });
}

function onPhoneNumberInput(event: Event) {
  const target = event.target as HTMLInputElement;
  let value = target.value;
  // 空白を削除
  value = value.replace(/\s/g, "");
  // 数字以外の入力を無視
  if (!/^\d*$/.test(value)) {
    value = value.replace(/\D/g, "");
  }

  if (value.length > 11) {
    value = value.slice(0, 11);
  }
  value = formatPhoneNumber(value);
  phoneNumber.value = value;
  target.value = value;
}
function formatPhoneNumber(value: string) {
  // 3桁 4桁 4桁に区切る。11桁ない場合も考慮
  const first = value.slice(0, 3);
  const second = value.slice(3, 7);
  const third = value.slice(7, 11);
  return [first, second, third].filter(v => v).join(" ");
}
</script>

<template>
  <div class="content-wrapper vh-100">
    <Header>
      <template v-slot:left>
        <FeatherIcon icon="chevron-left" @click="$router.back()" />
      </template>
      <span>電話番号認証</span>
    </Header>
    <CustomValidationObserver
      v-slot="{ handleSubmit, valid }"
      ref="form"
      tag="div"
      class="w-100"
    >
      <BForm @submit.stop.prevent="handleSubmit(nextStep)">
        <main>
          <div class="description">
            電話番号を入力しましょう
          </div>
          <ValidationProvider
            v-slot="validationState"
            name="電話番号"
            rules="phonenumber_with_space"
            vid="phoneNumber"
            class="w-100"
          >
            <input
              ref="phoneNumberInputRef"
              :value="phoneNumber"
              @input="onPhoneNumberInput"
              type="tel"
              class="phone-number-input"
              placeholder="080 1234 5678"
            />
            <FormInvalidFeedback
              v-if="validationState"
              :validation-state="validationState"
              :state="false"
            />
          </ValidationProvider>
          <AppButton
            @click="nextStep"
            rounded
            :disabled="!valid"
            class="next-button"
            >次へ</AppButton
          >
        </main>
      </BForm>
    </CustomValidationObserver>
  </div>
</template>

<style lang="scss" scoped>
.attention {
  margin-top: 1rem;
  margin-bottom: 2.1rem;
}

main {
  display: flex;
  flex-direction: column;
  gap: 40px;
  align-items: center;
  justify-content: center;
  padding: 0 24px;
  margin: 80px 0 32px;
  color: #40220f;
}

.description {
  font-size: 18px;
  font-weight: 600;
  line-height: 1;
  text-align: center;
}

.form-group {
  width: 100%;
}

.phone-number-input {
  width: 100%;
  height: 48px;
  padding: 8px 16px;
  font-size: 18px;
  font-weight: 700;
  color: #000;
  border: 1px solid #e5e5e5;
  border-radius: 5px;

  &::placeholder {
    color: #ccc !important;
  }
}

.next-button {
  width: 100%;
  height: 48px;
  margin-top: 0.9rem;
}

.content-wrapper {
  background-color: #f2f2f2;

  .phone-number-input {
    background-color: $white;
  }

  .next-button {
    color: $white;

    &:disabled {
      background-color: #c4c4c4;
    }
  }
}
</style>
